import { useAui } from "@assistant-ui/react";
import type { WidgetInstance } from "@kourai/api/home-board";
import { GripVerticalIcon, XIcon } from "lucide-react";
import {
  Component,
  type ComponentType,
  type KeyboardEvent,
  lazy,
  type ReactNode,
  Suspense,
  useMemo,
  useState,
} from "react";
import { browser } from "wxt/browser";
import { useWidgetData } from "@/hooks/use-widget-data";
import { gateBrowser, patchConfig, resolveConfig } from "@/lib/home/registry";
import {
  OPTIONAL_PERMISSIONS,
  sizeOf,
  WIDGET_SIZES,
  type WidgetDefinition,
  type WidgetHost,
  type WidgetPermission,
  type WidgetProps,
  type WidgetSize,
} from "@/lib/home/widget";
import { inExtension } from "@/lib/platform";
import { cn } from "@/lib/utils";

// biome-ignore lint/suspicious/noExplicitAny: components of every config type share the cache
type AnyComponent = ComponentType<WidgetProps<any>>;
const components = new Map<string, AnyComponent>();

/** One lazy component per widget type, so a type's code downloads once, when first shown. */
function componentFor(def: WidgetDefinition<unknown>): AnyComponent {
  let c = components.get(def.type);
  if (!c) {
    c = lazy(def.load as () => Promise<{ default: AnyComponent }>);
    components.set(def.type, c);
  }
  return c;
}

/** A widget that throws (or fails to load) becomes a quiet tile instead of breaking the board. */
class WidgetBoundary extends Component<
  { children: ReactNode; title: string },
  { failed: boolean }
> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override componentDidCatch(error: unknown) {
    console.error(`[home] widget ${this.props.title} failed`, error);
  }
  override render() {
    return this.state.failed ? <Unavailable /> : this.props.children;
  }
}

function Unavailable({ message = "This widget isn't available." }: { message?: string }) {
  return (
    <div className="border-border text-muted-foreground flex h-full items-start border-t pt-3 text-[13px]">
      {message}
    </div>
  );
}

/** Painted at the tile's stored size while its code loads, so nothing shifts. */
function TileSkeleton() {
  return (
    <div aria-hidden className="border-border h-full border-t pt-3">
      <div className="bg-accent h-3 w-24" />
    </div>
  );
}

const SIZE_LABEL: Record<WidgetSize, string> = { s: "S", m: "M", l: "L", w: "W", xl: "XL" };

export type TileContext = {
  workspace: { id: string; name: string };
  user: { id: string; name: string };
  editing: boolean;
  onConfig: (id: string, config: Record<string, unknown>) => void;
  onRemove: (instance: WidgetInstance) => void;
  onResize: (id: string, size: WidgetSize) => void;
  onKey: (id: string, e: KeyboardEvent<HTMLElement>) => void;
  /** Signed out preview: content lives in memory only, nothing is loaded or saved. */
  local?: boolean;
};

/** One widget on the board: the tile chrome in Customize, the widget itself always. */
export function WidgetTile({
  instance,
  def,
  ctx,
}: {
  instance: WidgetInstance;
  def: WidgetDefinition<unknown> | undefined;
  ctx: TileContext;
}) {
  const title = def?.title ?? "Widget";
  const label = `${title}, ${instance.w} by ${instance.h}, column ${instance.x + 1}, row ${instance.y}`;
  return (
    <section
      aria-label={ctx.editing ? label : undefined}
      aria-roledescription={ctx.editing ? "widget" : undefined}
      // In Customize the tile itself takes focus: arrows move it, Shift+arrows resize it.
      tabIndex={ctx.editing ? 0 : undefined}
      onKeyDown={ctx.editing ? (e) => ctx.onKey(instance.id, e) : undefined}
      data-widget={instance.type}
      data-widget-id={instance.id}
      className={cn(
        "bg-background flex h-full min-w-0 flex-col overflow-hidden outline-none",
        ctx.editing && "border-foreground focus-visible:border-ring border",
      )}
    >
      {ctx.editing && (
        <div className="board-drag-handle border-border text-muted-foreground flex h-8 shrink-0 cursor-grab items-center gap-1 border-b px-1.5 text-[13px] select-none active:cursor-grabbing">
          <GripVerticalIcon aria-hidden className="size-3.5 shrink-0" />
          <span className="min-w-0 flex-1 truncate">{title}</span>
          {def && def.sizes.length > 1 && (
            <fieldset className="flex gap-0.5">
              <legend className="sr-only">{`${title} size`}</legend>
              {def.sizes.map((s) => (
                <button
                  key={s}
                  type="button"
                  aria-pressed={sizeOf(instance) === s}
                  aria-label={`${title} size ${SIZE_LABEL[s]}, ${WIDGET_SIZES[s].w} by ${WIDGET_SIZES[s].h}`}
                  onClick={() => ctx.onResize(instance.id, s)}
                  className="border-border aria-pressed:border-foreground aria-pressed:text-foreground hover:text-brand border px-1 text-[11px] leading-4"
                >
                  {SIZE_LABEL[s]}
                </button>
              ))}
            </fieldset>
          )}
          <button
            type="button"
            aria-label={`Remove ${title}`}
            onClick={() => ctx.onRemove(instance)}
            className="hover:text-brand ms-1 p-0.5"
          >
            <XIcon aria-hidden className="size-3.5" />
          </button>
        </div>
      )}
      <div className={cn("min-h-0 flex-1", ctx.editing ? "overflow-hidden p-2" : "overflow-auto")}>
        {def ? <WidgetBody instance={instance} def={def} ctx={ctx} /> : <Unavailable />}
      </div>
    </section>
  );
}

/** `host.requestPermission`: only a declared permission, and only in the extension. */
async function requestWidgetPermission(
  declared: readonly WidgetPermission[],
  p: WidgetPermission,
): Promise<boolean> {
  if (!declared.includes(p) || !inExtension()) return false;
  if (!OPTIONAL_PERMISSIONS.includes(p)) return true;
  try {
    const want = { permissions: [p] } as Parameters<typeof browser.permissions.request>[0];
    return (await browser.permissions.contains(want)) || (await browser.permissions.request(want));
  } catch {
    return false;
  }
}

/** `host.useData` for one widget instance: a hook the widget calls during render. */
function dataHook(p: Parameters<typeof useWidgetData>[0]) {
  return function useData<T>(initial: T) {
    return useWidgetData<T>(p, initial);
  };
}

function useMemoryData<T>(initial: T) {
  return useState<T>(initial);
}

function WidgetBody({
  instance,
  def,
  ctx,
}: {
  instance: WidgetInstance;
  def: WidgetDefinition<unknown>;
  ctx: TileContext;
}) {
  const aui = useAui();
  const resolved = useMemo(() => resolveConfig(def, instance), [def, instance]);
  const owner = def.data === "workspace" ? "workspace" : ctx.user.id;
  const host: WidgetHost<unknown> = useMemo(
    () => ({
      widgetId: instance.id,
      workspace: ctx.workspace,
      user: ctx.user,
      useData: ctx.local
        ? useMemoryData
        : dataHook({
            workspaceId: ctx.workspace.id,
            owner,
            widgetId: instance.id,
            type: def.type,
            shared: def.data === "workspace",
          }),
      setConfig: (patch) => {
        if (!resolved.ok) return;
        const next = patchConfig(def, resolved.config, patch as Partial<unknown>);
        if (next) ctx.onConfig(instance.id, next as Record<string, unknown>);
      },
      ask: (prompt) => {
        aui.composer().setText(prompt);
        aui.composer().send();
      },
      openUrl: (url, options) => {
        if (options?.newTab) window.open(url, "_blank", "noopener");
        else window.location.assign(url);
      },
      browser: gateBrowser(def.permissions, (inExtension() ? browser : {}) as never),
      requestPermission: (p) => requestWidgetPermission(def.permissions, p),
      editing: ctx.editing,
    }),
    [ctx, def, instance.id, owner, resolved, aui],
  );
  if (!resolved.ok) return <Unavailable message={resolved.error} />;
  const Widget = componentFor(def);
  return (
    <WidgetBoundary title={def.title}>
      <Suspense fallback={<TileSkeleton />}>
        <Widget
          config={resolved.config}
          size={sizeOf(instance)}
          cells={{ w: instance.w, h: instance.h }}
          host={host}
        />
      </Suspense>
    </WidgetBoundary>
  );
}
